import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, Briefcase, Calendar, DollarSign, Star, 
  LogOut, Search, Trash2, Eye, TrendingUp, Building2,
  UserCheck, FileText, MessageSquare, RefreshCw,
  X, Check, AlertTriangle, Megaphone, Download,
  ChevronDown, ChevronUp, Send, Mail, FileDown,
  Plus, ArrowUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import DOMPurify from 'dompurify';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [corporates, setCorporates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [allAds, setAllAds] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });
  
  // Comments state
  const [commentPanel, setCommentPanel] = useState({ open: false, entityType: '', entityId: '', entityName: '' });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Email template state
  const [emailDialog, setEmailDialog] = useState({ open: false, recipientEmail: '', recipientName: '' });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [emailVars, setEmailVars] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Add user
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', phone: '', user_type: 'trainer', password: 'Temp@1234' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, trainersRes, corporatesRes, jobsRes, bookingsRes, applicationsRes, reviewsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/trainers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/corporates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setTrainers(trainersRes.data);
      setCorporates(corporatesRes.data);
      setJobs(jobsRes.data);
      setBookings(bookingsRes.data);
      setApplications(applicationsRes.data);
      setReviews(reviewsRes.data);
      setUsers(usersRes.data);

      try {
        const adsRes = await axios.get(`${API}/ads/all`, { headers: { Authorization: `Bearer ${token}` } });
        setAllAds(adsRes.data);
      } catch (e) { /* no ads */ }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (user?.user_type !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchAllData();
  }, [user, navigate, fetchAllData]);

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'user') {
        await axios.delete(`${API}/admin/users/${id}`, { headers });
      } else if (type === 'job') {
        await axios.delete(`${API}/admin/jobs/${id}`, { headers });
      } else if (type === 'review') {
        await axios.delete(`${API}/admin/reviews/${id}`, { headers });
      }
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleUpdateJobStatus = async (jobId, status) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/status?status=${status}`, {}, { headers });
      toast.success('Job status updated');
      fetchAllData();
    } catch { toast.error('Failed to update job status'); }
  };

  // ==================== COMMENTS ====================
  const openComments = async (entityType, entityId, entityName) => {
    setCommentPanel({ open: true, entityType, entityId, entityName });
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/admin/comments/${entityType}/${entityId}`, { headers });
      setComments(res.data);
    } catch { setComments([]); }
    finally { setLoadingComments(false); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/admin/comments`, {
        entity_type: commentPanel.entityType,
        entity_id: commentPanel.entityId,
        comment: newComment
      }, { headers });
      setNewComment('');
      const res = await axios.get(`${API}/admin/comments/${commentPanel.entityType}/${commentPanel.entityId}`, { headers });
      setComments(res.data);
      toast.success('Comment added');
      fetchAllData();
    } catch { toast.error('Failed to add comment'); }
  };

  const deleteCommentHandler = async (commentId) => {
    try {
      await axios.delete(`${API}/admin/comments/${commentId}`, { headers });
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete comment'); }
  };

  // ==================== INVOICE ====================
  const downloadInvoice = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}/invoice`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `INV-${bookingId.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch { toast.error('Failed to download invoice'); }
  };

  // ==================== EMAIL TEMPLATES ====================
  const openEmailDialog = async (email, name) => {
    setEmailDialog({ open: true, recipientEmail: email, recipientName: name });
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { headers });
      setEmailTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
  };

  const selectTemplate = async (templateId) => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/${templateId}`, { headers });
      setSelectedTemplate(res.data);
      setTemplatePreview(res.data);
      const vars = {};
      (res.data.variables || []).forEach(v => {
        if (v === 'recipient_name') vars[v] = emailDialog.recipientName;
        else vars[v] = '';
      });
      setEmailVars(vars);
    } catch { toast.error('Failed to load template'); }
  };

  const sendEmail = async () => {
    if (!selectedTemplate) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-email`, {
        template_id: selectedTemplate.id,
        recipient_email: emailDialog.recipientEmail,
        recipient_name: emailDialog.recipientName,
        variables: emailVars
      }, { headers });
      toast.success(`Email sent to ${emailDialog.recipientEmail}`);
      setEmailDialog({ open: false, recipientEmail: '', recipientName: '' });
      setSelectedTemplate(null);
      setTemplatePreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSendingEmail(false); }
  };

  // ==================== SORTING ====================
  const sortData = useCallback((data, key) => {
    if (!sortConfig.key || sortConfig.key !== key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[key] ?? 0;
      const bVal = b[key] ?? 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === 'asc' 
        ? String(aVal).localeCompare(String(bVal)) 
        : String(bVal).localeCompare(String(aVal));
    });
  }, [sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortHeader = ({ label, sortKey }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => handleSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3 text-slate-400" />
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />
        )}
      </div>
    </th>
  );

  // ==================== FILTERS ====================
  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = userTypeFilter === 'all' || u.user_type === userTypeFilter;
    return matchesSearch && matchesType;
  });

  const filteredJobs = jobs.filter(j => {
    const matchesSearch = !searchQuery || 
      j.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      j.corporate_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || j.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredTrainers = trainers.filter(t => {
    return !searchQuery || 
      t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.skills?.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const filteredCorporates = corporates.filter(c => {
    return !searchQuery || 
      c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.industry?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const sortedTrainers = sortConfig.key ? sortData(filteredTrainers, sortConfig.key) : filteredTrainers;
  const sortedCorporates = sortConfig.key ? sortData(filteredCorporates, sortConfig.key) : filteredCorporates;

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const exportCSV = async (endpoint, label) => {
    try {
      const res = await axios.get(`${API}/admin/export/${endpoint}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${endpoint}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} CSV downloaded!`);
    } catch { toast.error(`Failed to export ${label}`); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <span className="text-slate-600 font-medium">Loading admin dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-outfit font-bold text-lg">Admin Dashboard</span>
                <span className="text-slate-400 text-sm ml-2">Training Solutions</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={fetchAllData} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={<UserCheck className="w-5 h-5 text-blue-600" />} label="Trainers" value={stats?.total_trainers || 0} subtext={`+${stats?.new_trainers_this_week || 0} this week / +${stats?.new_trainers_this_month || 0} this month`} bgColor="bg-blue-50" />
          <StatCard icon={<Building2 className="w-5 h-5 text-emerald-600" />} label="Corporates" value={stats?.total_corporates || 0} subtext={`+${stats?.new_corporates_this_week || 0} this week / +${stats?.new_corporates_this_month || 0} this month`} bgColor="bg-emerald-50" />
          <StatCard icon={<Briefcase className="w-5 h-5 text-purple-600" />} label="Training Reqs" value={stats?.total_jobs || 0} subtext={`${stats?.open_jobs || 0} open`} bgColor="bg-purple-50" />
          <StatCard icon={<Calendar className="w-5 h-5 text-amber-600" />} label="Bookings" value={stats?.total_bookings || 0} subtext={`${stats?.confirmed_bookings || 0} confirmed | ${stats?.pending_bookings || 0} pending | ${stats?.completed_bookings || 0} done`} bgColor="bg-amber-50" />
          <StatCard icon={<DollarSign className="w-5 h-5 text-green-600" />} label="Revenue" value={`₹${(stats?.total_revenue || 0).toLocaleString()}`} subtext={`₹${(stats?.weekly_revenue || 0).toLocaleString()} this week | ₹${(stats?.monthly_revenue || 0).toLocaleString()} this month`} bgColor="bg-green-50" />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <QuickStat icon={<FileText className="w-6 h-6 text-orange-600" />} color="orange" value={stats?.total_applications || 0} label="Applications" badge={`${stats?.pending_applications || 0} pending`} />
          <QuickStat icon={<Star className="w-6 h-6 text-yellow-600" />} color="yellow" value={stats?.total_reviews || 0} label="Reviews" />
          <QuickStat icon={<TrendingUp className="w-6 h-6 text-blue-600" />} color="blue" value={stats?.new_jobs_this_week || 0} label="New Reqs This Week" />
        </div>

        {/* Exports */}
        <div className="flex flex-wrap gap-3 mb-6" data-testid="export-reports">
          {[
            { label: 'Bookings', endpoint: 'bookings' },
            { label: 'Users', endpoint: 'users' },
            { label: 'Earnings', endpoint: 'earnings' },
          ].map(({ label, endpoint }) => (
            <Button key={endpoint} variant="outline" size="sm" onClick={() => exportCSV(endpoint, label)} className="text-slate-600" data-testid={`export-${endpoint}-btn`}>
              <Download className="w-4 h-4 mr-2" /> Export {label} CSV
            </Button>
          ))}
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="trainers" className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-lg flex-wrap">
              <TabsTrigger value="trainers" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-trainers">
                <UserCheck className="w-4 h-4 mr-2" /> Trainers ({trainers.length})
              </TabsTrigger>
              <TabsTrigger value="companies" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-companies">
                <Building2 className="w-4 h-4 mr-2" /> Companies ({corporates.length})
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" /> Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="jobs" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-jobs">
                <Briefcase className="w-4 h-4 mr-2" /> Requirements ({jobs.length})
              </TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-bookings">
                <Calendar className="w-4 h-4 mr-2" /> Bookings ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-reviews">
                <Star className="w-4 h-4 mr-2" /> Reviews ({reviews.length})
              </TabsTrigger>
              <TabsTrigger value="email" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-email">
                <Mail className="w-4 h-4 mr-2" /> Email
              </TabsTrigger>
              <TabsTrigger value="ads" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-ads">
                <Megaphone className="w-4 h-4 mr-2" /> Ads ({allAds.length})
              </TabsTrigger>
            </TabsList>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 w-64" data-testid="admin-search-input" />
            </div>
          </div>

          {/* ==================== TRAINERS TAB ==================== */}
          <TabsContent value="trainers">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Trainer Dashboard</h3>
                <span className="text-sm text-slate-500">{filteredTrainers.length} trainers</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="trainers-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Trainer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Skills</th>
                      <SortHeader label="Rating" sortKey="rating" />
                      <SortHeader label="Exp (yrs)" sortKey="experience_years" />
                      <SortHeader label="Rate/hr" sortKey="hourly_rate" />
                      <SortHeader label="Bookings" sortKey="bookings_count" />
                      <SortHeader label="Revenue" sortKey="total_revenue" />
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedTrainers.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                              {t.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900 text-sm">{t.name}</div>
                              <div className="text-xs text-slate-500">{t.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{t.location || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(t.skills || []).slice(0, 3).map(s => (
                              <span key={s} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded-full">{s}</span>
                            ))}
                            {(t.skills || []).length > 3 && <span className="text-xs text-slate-400">+{t.skills.length - 3}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                            <span className="text-sm font-medium">{t.rating?.toFixed(1) || '0.0'}</span>
                            <span className="text-xs text-slate-400">({t.total_reviews || 0})</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 text-center">{t.experience_years || 0}</td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">₹{(t.hourly_rate || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="font-medium text-slate-900">{t.bookings_count || 0}</span>
                          <span className="text-xs text-slate-400 ml-1">({t.completed_trainings || 0} done)</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-emerald-700">₹{(t.total_revenue || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/trainer/${t.id}`)} data-testid={`view-trainer-${t.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEmailDialog(t.email, t.name)} data-testid={`email-trainer-${t.id}`}>
                              <Mail className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="relative" onClick={() => openComments('trainer', t.id, t.name)} data-testid={`comment-trainer-${t.id}`}>
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                              {(t.comments_count || 0) > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[9px] rounded-full flex items-center justify-center">{t.comments_count}</span>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTrainers.length === 0 && <div className="p-8 text-center text-slate-400">No trainers found</div>}
              </div>
            </div>
          </TabsContent>

          {/* ==================== COMPANIES TAB ==================== */}
          <TabsContent value="companies">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Company Dashboard</h3>
                <span className="text-sm text-slate-500">{filteredCorporates.length} companies</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="companies-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Industry</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                      <SortHeader label="Jobs Posted" sortKey="jobs_posted" />
                      <SortHeader label="Active Jobs" sortKey="active_jobs" />
                      <SortHeader label="Bookings" sortKey="bookings_count" />
                      <SortHeader label="Total Spend" sortKey="total_spend" />
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedCorporates.map(c => (
                      <tr key={c.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                              {c.company_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900 text-sm">{c.company_name}</div>
                              <div className="text-xs text-slate-500">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{c.industry || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{c.location || '-'}</td>
                        <td className="px-4 py-3 text-sm text-center font-medium text-slate-900">{c.jobs_posted || 0}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">{c.active_jobs || 0}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-medium text-slate-900">{c.bookings_count || 0}</td>
                        <td className="px-4 py-3 text-sm font-medium text-emerald-700">₹{(c.total_spend || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEmailDialog(c.email, c.company_name)} data-testid={`email-corp-${c.id}`}>
                              <Mail className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="relative" onClick={() => openComments('corporate', c.id, c.company_name)} data-testid={`comment-corp-${c.id}`}>
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                              {(c.comments_count || 0) > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[9px] rounded-full flex items-center justify-center">{c.comments_count}</span>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredCorporates.length === 0 && <div className="p-8 text-center text-slate-400">No companies found</div>}
              </div>
            </div>
          </TabsContent>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
                <h3 className="font-semibold text-slate-900">All Users</h3>
                <div className="flex items-center gap-3">
                  <select value={userTypeFilter} onChange={(e) => setUserTypeFilter(e.target.value)} className="h-9 px-3 text-sm rounded-lg border border-slate-200">
                    <option value="all">All Types</option>
                    <option value="trainer">Trainers</option>
                    <option value="corporate">Corporates</option>
                    <option value="admin">Admins</option>
                  </select>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowAddUser(!showAddUser)} data-testid="add-user-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add User
                  </Button>
                </div>
              </div>

              {/* Add User Form */}
              {showAddUser && (
                <div className="p-4 bg-blue-50/50 border-b border-blue-100 space-y-3" data-testid="add-user-form">
                  <div className="grid md:grid-cols-5 gap-3">
                    <Input placeholder="Full Name *" value={newUserForm.name} onChange={(e) => setNewUserForm(f => ({...f, name: e.target.value}))} data-testid="new-user-name" />
                    <Input placeholder="Email *" type="email" value={newUserForm.email} onChange={(e) => setNewUserForm(f => ({...f, email: e.target.value}))} data-testid="new-user-email" />
                    <Input placeholder="Phone" value={newUserForm.phone} onChange={(e) => setNewUserForm(f => ({...f, phone: e.target.value}))} data-testid="new-user-phone" />
                    <select value={newUserForm.user_type} onChange={(e) => setNewUserForm(f => ({...f, user_type: e.target.value}))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="new-user-type">
                      <option value="trainer">Trainer</option>
                      <option value="corporate">Corporate</option>
                    </select>
                    <Input placeholder="Password" value={newUserForm.password} onChange={(e) => setNewUserForm(f => ({...f, password: e.target.value}))} data-testid="new-user-password" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                      if (!newUserForm.name || !newUserForm.email) { toast.error('Name and email required'); return; }
                      try {
                        await axios.post(`${API}/admin/users`, newUserForm, { headers });
                        toast.success('User created!');
                        setNewUserForm({ name: '', email: '', phone: '', user_type: 'trainer', password: 'Temp@1234' });
                        setShowAddUser(false);
                        fetchAllData();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
                    }} data-testid="submit-new-user-btn">
                      Create User
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Joined</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold ${u.user_type === 'trainer' ? 'bg-blue-600' : u.user_type === 'corporate' ? 'bg-emerald-600' : 'bg-slate-900'}`}>
                              {u.name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${u.user_type === 'trainer' ? 'bg-blue-50 text-blue-700' : u.user_type === 'corporate' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                            {u.user_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {u.user_type === 'trainer' && (
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/trainer/${trainers.find(t => t.user_id === u.id)?.id}`)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openComments('user', u.id, u.name)} data-testid={`comment-user-${u.id}`}>
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                            </Button>
                            {u.user_type !== 'admin' && (
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteDialog({ open: true, type: 'user', id: u.id, name: u.name })}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ==================== JOBS TAB ==================== */}
          <TabsContent value="jobs">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">All Training Requirements</h3>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-sm rounded-lg border border-slate-200">
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="filled">Filled</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Applications</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Budget</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Posted</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredJobs.map(job => (
                      <tr key={job.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{job.title}</td>
                        <td className="px-4 py-3 text-slate-600">{job.corporate_name}</td>
                        <td className="px-4 py-3">
                          <select value={job.status} onChange={(e) => handleUpdateJobStatus(job.id, e.target.value)}
                            className={`px-2 py-1 text-xs rounded-full font-medium border-0 ${job.status === 'open' ? 'bg-emerald-50 text-emerald-700' : job.status === 'filled' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                            <option value="open">Open</option>
                            <option value="filled">Filled</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{job.applications_count}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">₹{job.budget?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{new Date(job.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/job/${job.id}`)}><Eye className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openComments('job', job.id, job.title)} data-testid={`comment-job-${job.id}`}>
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteDialog({ open: true, type: 'job', id: job.id, name: job.title })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ==================== BOOKINGS TAB ==================== */}
          <TabsContent value="bookings">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">All Bookings</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bookings-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Job</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Trainer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bookings.map(booking => (
                      <tr key={booking.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{booking.job_title}</td>
                        <td className="px-4 py-3 text-slate-600">{booking.trainer_name}</td>
                        <td className="px-4 py-3 text-slate-600">{booking.corporate_name}</td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{new Date(booking.start_datetime).toLocaleDateString()}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">₹{booking.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${booking.status === 'confirmed' ? 'bg-blue-50 text-blue-700' : booking.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadInvoice(booking.id)} data-testid={`invoice-${booking.id}`} title="Download Invoice">
                              <FileDown className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openComments('booking', booking.id, booking.job_title)} data-testid={`comment-booking-${booking.id}`}>
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {bookings.length === 0 && <div className="p-8 text-center text-slate-400">No bookings found</div>}
              </div>
            </div>
          </TabsContent>

          {/* ==================== REVIEWS TAB ==================== */}
          <TabsContent value="reviews">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">All Reviews</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {reviews.map(review => (
                  <div key={review.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-slate-900">{review.corporate_name}</span>
                          <span className="text-slate-400">→</span>
                          <span className="text-slate-600">{review.trainer_name}</span>
                          <div className="flex items-center gap-1 ml-2">
                            {[1,2,3,4,5].map(star => (
                              <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm">{review.comment}</p>
                        <p className="text-slate-400 text-xs mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteDialog({ open: true, type: 'review', id: review.id, name: 'this review' })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && <div className="p-8 text-center text-slate-400">No reviews found</div>}
              </div>
            </div>
          </TabsContent>

          {/* ==================== EMAIL TEMPLATES TAB ==================== */}
          <TabsContent value="email">
            <EmailTemplatesPanel token={token} />
          </TabsContent>

          {/* ==================== ADS TAB ==================== */}
          <TabsContent value="ads">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Advertisement Management</h3>
                <p className="text-sm text-slate-500">Approve or reject company advertisements.</p>
              </div>
              {allAds.length === 0 ? (
                <div className="p-12 text-center">
                  <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No advertisements submitted yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {allAds.map(ad => (
                    <div key={ad.id} className="p-4 hover:bg-slate-50" data-testid={`admin-ad-${ad.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">{ad.company_name?.charAt(0)}</div>
                            <div>
                              <h4 className="font-medium text-slate-900">{ad.headline}</h4>
                              <p className="text-xs text-slate-500">{ad.company_name}</p>
                            </div>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${ad.status === 'active' ? 'bg-emerald-50 text-emerald-700' : ad.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{ad.status}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1 ml-11">{ad.description}</p>
                          {ad.link_url && <p className="text-xs text-blue-500 mt-1 ml-11">{ad.link_url}</p>}
                          {ad.training_types?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2 ml-11">
                              {ad.training_types.map(t => <span key={t} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{t}</span>)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {ad.status !== 'active' && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-ad-${ad.id}`}
                              onClick={async () => { try { await axios.put(`${API}/ads/${ad.id}/status?status=active`, {}, { headers }); toast.success('Ad approved'); fetchAllData(); } catch { toast.error('Failed to approve'); } }}>
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          )}
                          {ad.status !== 'rejected' && (
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-ad-${ad.id}`}
                              onClick={async () => { try { await axios.put(`${API}/ads/${ad.id}/status?status=rejected`, {}, { headers }); toast.success('Ad rejected'); fetchAllData(); } catch { toast.error('Failed to reject'); } }}>
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                            onClick={async () => { try { await axios.delete(`${API}/ads/${ad.id}`, { headers }); toast.success('Ad deleted'); fetchAllData(); } catch { toast.error('Failed to delete'); } }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== COMMENTS SLIDE PANEL ==================== */}
      <Dialog open={commentPanel.open} onOpenChange={(open) => { if (!open) setCommentPanel({ open: false, entityType: '', entityId: '', entityName: '' }); }}>
        <DialogContent className="sm:max-w-lg" data-testid="comments-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Notes: {commentPanel.entityName}
            </DialogTitle>
            <DialogDescription>
              Admin notes for this {commentPanel.entityType}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {loadingComments ? (
              <div className="text-center py-4 text-slate-500">Loading...</div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No notes yet. Add the first one.</p>
              </div>
            ) : (
              comments.map(c => (
                <div key={c.id} className="bg-slate-50 rounded-lg p-3 group" data-testid={`comment-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-900">{c.admin_name}</span>
                        <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-700">{c.comment}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 text-red-500" onClick={() => deleteCommentHandler(c.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="Add a note..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addComment()}
              data-testid="comment-input"
            />
            <Button onClick={addComment} disabled={!newComment.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="add-comment-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== EMAIL SEND DIALOG ==================== */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => { if (!open) { setEmailDialog({ open: false, recipientEmail: '', recipientName: '' }); setSelectedTemplate(null); setTemplatePreview(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Send Email to {emailDialog.recipientName}
            </DialogTitle>
            <DialogDescription>{emailDialog.recipientEmail}</DialogDescription>
          </DialogHeader>

          {!selectedTemplate ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Choose a template:</p>
              {emailTemplates.map(t => (
                <button key={t.id} onClick={() => selectTemplate(t.id)} className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors" data-testid={`template-${t.id}`}>
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500 mt-1">{t.subject}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.variables.map(v => <span key={v} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{`{{${v}}}`}</span>)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => { setSelectedTemplate(null); setTemplatePreview(null); }} className="text-slate-500">
                ← Back to templates
              </Button>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Subject</label>
                  <div className="mt-1 p-2 bg-slate-50 rounded-lg text-sm text-slate-700">{selectedTemplate.subject}</div>
                </div>

                {(selectedTemplate.variables || []).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Variables</label>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedTemplate.variables.map(v => (
                        <div key={v}>
                          <label className="text-xs text-slate-500 mb-1 block">{v.replace(/_/g, ' ')}</label>
                          <Input
                            value={emailVars[v] || ''}
                            onChange={(e) => setEmailVars(prev => ({ ...prev, [v]: e.target.value }))}
                            placeholder={v}
                            data-testid={`var-${v}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Preview</label>
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((() => {
                      let html = selectedTemplate.body;
                      Object.entries(emailVars).forEach(([k, v]) => {
                        html = html.replaceAll(`{{${k}}}`, v || `{{${k}}}`);
                      });
                      return html;
                    })()) }} />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setEmailDialog({ open: false, recipientEmail: '', recipientName: '' }); setSelectedTemplate(null); }}>Cancel</Button>
                <Button onClick={sendEmail} disabled={sendingEmail} className="bg-blue-600 hover:bg-blue-700" data-testid="send-email-btn">
                  {sendingEmail ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== DELETE DIALOG ==================== */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: '', id: '', name: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

const StatCard = ({ icon, label, value, subtext, bgColor }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}>{icon}</div>
    </div>
    <div className="font-outfit text-2xl font-bold text-slate-900">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
    {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
  </div>
);

const QuickStat = ({ icon, color, value, label, badge }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
    <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>{icon}</div>
    <div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
    {badge && (
      <div className="ml-auto">
        <span className={`px-2 py-1 bg-${color}-100 text-${color}-700 text-xs rounded-full font-medium`}>{badge}</span>
      </div>
    )}
  </div>
);

// Standalone Email Templates Panel for the Email tab
const EmailTemplatesPanel = ({ token: panelToken }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [vars, setVars] = useState({});
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [sending, setSending] = useState(false);
  const panelHeaders = { Authorization: `Bearer ${panelToken}` };

  useEffect(() => {
    axios.get(`${API}/admin/email-templates`, { headers: { Authorization: `Bearer ${panelToken}` } })
      .then(res => setTemplates(res.data))
      .catch(() => {});
  }, [panelToken]);

  const selectTpl = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/${id}`, { headers: panelHeaders });
      setSelected(res.data);
      const v = {};
      (res.data.variables || []).forEach(varName => { v[varName] = ''; });
      setVars(v);
    } catch { toast.error('Failed to load template'); }
  };

  const handleSend = async () => {
    if (!recipientEmail || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/send-email`, {
        template_id: selected.id,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        variables: vars
      }, { headers: panelHeaders });
      toast.success(`Email sent to ${recipientEmail}`);
      setRecipientEmail('');
      setRecipientName('');
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Templates list */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Email Templates</h3>
            <p className="text-xs text-slate-500 mt-1">Select a template to preview and send</p>
          </div>
          <div className="divide-y divide-slate-100">
            {templates.map(t => (
              <button key={t.id} onClick={() => selectTpl(t.id)} className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${selected?.id === t.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`} data-testid={`tpl-${t.id}`}>
                <div className="font-medium text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.subject}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview and Send */}
      <div className="lg:col-span-2">
        {!selected ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Select a template from the left to preview</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{selected.name}</h3>
              <p className="text-sm text-slate-500">{selected.subject}</p>
            </div>
            <div className="p-4 space-y-4">
              {/* Recipient */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Recipient Email</label>
                  <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="email@example.com" data-testid="tpl-recipient-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Recipient Name</label>
                  <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="John Doe" data-testid="tpl-recipient-name" />
                </div>
              </div>

              {/* Variables */}
              {(selected.variables || []).length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-2">Template Variables</label>
                  <div className="grid grid-cols-2 gap-3">
                    {selected.variables.map(v => (
                      <div key={v}>
                        <label className="text-[10px] text-slate-400 uppercase">{v.replace(/_/g, ' ')}</label>
                        <Input value={vars[v] || ''} onChange={(e) => setVars(prev => ({ ...prev, [v]: e.target.value }))} placeholder={v} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-2">Preview</label>
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[350px] overflow-y-auto bg-white">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((() => {
                    let html = selected.body;
                    Object.entries(vars).forEach(([k, val]) => {
                      html = html.replaceAll(`{{${k}}}`, val || `{{${k}}}`);
                    });
                    if (recipientName) html = html.replaceAll('{{recipient_name}}', recipientName);
                    return html;
                  })()) }} />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSend} disabled={sending || !recipientEmail} className="bg-blue-600 hover:bg-blue-700" data-testid="tpl-send-btn">
                  {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
